<template>
  <variable-content :id="id">
    <template slot="desktop">
      <spinner-overlay v-if="!isEmpty(value) && guestLoaded === false" />
      <guest-search ref="guestSearch"
                    v-model="internalValue"
                    :label="$t('inputgroups.guestProfileEditor.internalValue')"
                    :disabled="searchDisabled || disabled"
                    :type="type" />
      <hm-col v-if="profile !== null" :grid="true" cols="12">
        <hm-gender v-if="profile.type === 'Guest'"
                   id="gender"
                   v-model="profile.gender"
                   :label="$t('inputgroups.guestProfileEditor.gender')"
                   :disabled="disabled"
                   name="gender"
                   cols="8" />

        <gravatar v-if="profile.type === 'Guest'"
                  id="gravatar"
                  :hash="profile.gravatar"
                  :type="profile.type"
                  :gender="profile.gender"
                  :size="48"
                  class="gravatar__wrapper--inline"
                  align="right"
                  cols="4"
                  rows="1" />

        <hm-input v-if="profile.type !== 'Guest'"
                  id="companyName"
                  v-model="profile.companyName"
                  :disabled="disabled || !profile.unlocked"
                  :label="$t('inputgroups.guestProfileEditor.companyName')"
                  :icons-right="profile.unlocked ? 0 : 1"
                  name="companyName">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('inputgroups.guestProfileEditor.unlockGuestName'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               v-if="!disabled && !profile.unlocked"
                               :border="true"
                               :disabled="false"
                               icon="unlock"
                               @click="profile.unlocked = true" />
          </template>
        </hm-input>
        <hm-input id="namePrefix"
                  v-model="profile.namePrefix"
                  :label="$t('inputgroups.guestProfileEditor.namePrefix')"
                  :context-help="$t('inputgroups.guestProfileEditor.namePrefix_contextHelp')"
                  :disabled="disabled"
                  name="namePrefix"
                  cols="12 6@tablet"/>
        <hm-input id="nameSuffix"
                  v-model="profile.nameSuffix"
                  :label="$t('inputgroups.guestProfileEditor.nameSuffix')"
                  :context-help="$t('inputgroups.guestProfileEditor.nameSuffix_contextHelp')"
                  :disabled="disabled"
                  name="nameSuffix"
                  cols="12 6@tablet"/>
        <hm-input id="firstName"
                  v-model="profile.firstName"
                  :disabled="disabled || !profile.unlocked"
                  :label="$t('inputgroups.guestProfileEditor.firstName')"
                  :icons-right="profile.unlocked ? 0 : 1"
                  name="firstName"
                  cols="12 6@tablet">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('inputgroups.guestProfileEditor.unlockGuestName'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               v-if="!disabled && !profile.unlocked"
                               :border="true"
                               :disabled="false"
                               icon="unlock"
                               @click="profile.unlocked = true" />
          </template>
        </hm-input>
        <hm-input id="lastName"
                  v-model="profile.lastName"
                  :disabled="disabled || !profile.unlocked"
                  :label="$t('inputgroups.guestProfileEditor.lastName')"
                  :icons-right="profile.unlocked ? 0 : 1"
                  name="lastName"
                  cols="12 6@tablet">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('inputgroups.guestProfileEditor.unlockGuestName'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               v-if="!disabled && !profile.unlocked"
                               :border="true"
                               :disabled="false"
                               icon="unlock"
                               @click="profile.unlocked = true" />
          </template>
        </hm-input>
        <hm-input-date v-if="profile.type === 'Guest'"
                       id="dateOfBirth"
                       :required="false"
                       :lazy="true"
                       :disabled="disabled"
                       v-model="profile.dateOfBirth"
                       :label="$t('inputgroups.guestProfileEditor.dateOfBirth')"
                       name="dateOfBirth" />
        <hm-input id="primaryEMailAddress"
                  :validate="'email'"
                  :lazy="true"
                  :disabled="disabled"
                  v-model="profile.primaryEMailAddress"
                  :label="$t('inputgroups.guestProfileEditor.primaryEMailAddress')"
                  name="primaryEMailAddress" />
        <hm-input id="primaryPhoneNumber"
                  :lazy="true"
                  :disabled="disabled"
                  v-model="profile.primaryPhoneNumber"
                  :label="$t('inputgroups.guestProfileEditor.primaryPhoneNumber')"
                  name="primaryPhoneNumber" />
        <hm-textarea id="address"
                     :lazy="true"
                     v-model="profile.primaryAddress.address"
                     :input-rows="2"
                     :label="$t('inputgroups.guestProfileEditor.address')"
                     :context-help="$t('inputgroups.guestProfileEditor.address_contextHelp')"
                     :disabled="disabled"
                     name="address"
                     hidden="mobile" />
        <hm-input-zip-city id="zipcity"
                           :zip="profile.primaryAddress.zipCode"
                           :city="profile.primaryAddress.city"
                           :country="profile.primaryAddress.country"
                           hidden="mobile"
                           @update:zip="profile.primaryAddress.zipCode = $event"
                           @update:city="profile.primaryAddress.city = $event"
                           @update:country="profile.primaryAddress.country = $event" />
        <hm-select id="country"
                   v-model="profile.primaryAddress.country"
                   :options="getI18nCountries($shortLocale, [profile.primaryAddress.country, profile.nationality])"
                   :label="$t('inputgroups.guestProfileEditor.country')"
                   :disabled="disabled"
                   name="country"
                   hidden="mobile" />
        <hm-select id="primaryCommunicationLanguage"
                   v-model="profile.primaryCommunicationLanguage"
                   :options="getI18nLanguages($shortLocale)"
                   :label="$t('inputgroups.guestProfileEditor.primaryCommunicationLanguage')"
                   :disabled="disabled"
                   name="primaryCommunicationLanguage" />
        <hm-select v-if="profile.type === 'Guest'"
                   id="nationality"
                   v-model="profile.nationality"
                   :options="getI18nCountries($shortLocale, [profile.nationality, profile.primaryAddress.country])"
                   :label="$t('inputgroups.guestProfileEditor.nationality')"
                   :disabled="disabled"
                   name="nationality"
                   hidden="mobile" />
        <hm-multiselect id="guestCategories"
                        v-model="profile.categories"
                        :options="hotelSettings.guestCategories.all()"
                        :label="$t('inputgroups.guestProfileEditor.guestCategories')"
                        :disabled="disabled"
                        name="guestCategories"
                        hidden="mobile"
                        label-expression="${option.name[$shortLocale]}" />
        <hm-textarea id="internalComment"
                     :lazy="true"
                     v-model="profile.internalComment"
                     :input-rows="2"
                     :label="$t('inputgroups.guestProfileEditor.internalComment')"
                     :disabled="disabled"
                     name="internalComment"
                     hidden="mobile" />
      </hm-col>
    </template>
  </variable-content>
</template>

<script>

import GridMixin from '@/mixins/GridMixin';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { isNil, isEmpty, isEqual, sampleSize, sortBy, isObject } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmTextarea from '../inputs/HmTextarea';
import GuestSearch from '../inputs/GuestSearch';
import SpinnerOverlay from '../util/SpinnerOverlay';
import TimerMixin from '../../../mixins/TimerMixin';
import HmInputZipCity from '../inputs/HmInputZipCity';

export default {
  name: 'ProfileEditor',
  components: { HmInputZipCity, SpinnerOverlay, GuestSearch, HmTextarea },
  mixins: [GridMixin, HotelmeisterMixin, SaveWatchMixin, TimerMixin],

  props: {
    id: {
      required: false,
      default: () => {
        const randomString = function (n) {
          return Array(n)
            .fill(null)
            .map(() => (Math.random() < 0.5 ? ((Math.random() * 100) % 25) + 'A'.charCodeAt(0) : ((Math.random() * 100) % 25) + 'a'.charCodeAt(0)))
            .map(a => String.fromCharCode(a))
            .join('');
        };
        return `comp_${randomString(5)}`;
      },
    },
    value: {
      required: true,
    },
    type: {
      type: String,
      default: 'Guest',
      validator: val => ['Guest', 'Company', 'TravelAgent', 'Other'].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchDisabled: {
      type: Boolean,
      default: false,
    },
  },

  autosave: [
    {
      name: 'profile',
      variable: 'profile',
      fn: function (cbSaveComplete) {
        if (!isEmpty(this.internalValue)) {
          this.axios.put(this.serviceUrls.guest.get(this, this.internalValue), this.profile)
            .then((response) => {
              cbSaveComplete();
              this.showMessages(response);
            })
            .catch((error) => {
              this.showMessages(error.response);
              cbSaveComplete();
            })
            .finally(() => {
              this.guestLoaded = true;
              this.updateSearch();
            });
        } else if (!this.isEmptyProfile(this.profile)) {
          this.axios.post(this.serviceUrls.guest.get(this, ''), this.profile)
            .then((response) => {
              this.profile.id = response.data.data;
              this.internalValue = response.data.data;
              this.profile.unlocked = true;
              cbSaveComplete();
              this.showMessages(response);
            })
            .catch((error) => {
              this.showMessages(error.response);
              cbSaveComplete();
            })
            .finally(() => {
              this.guestLoaded = true;
              this.updateSearch();
            });
        } else {
          cbSaveComplete();
        }
      },
    },
  ],

  data() {
    return {
      internalValue: this.value,
      profile: {
        type: this.type,
        primaryAddress: {},
        unlocked: true,
      },
      guestLoaded: false,
    };
  },

  watch: {
    value: function () {
      this.internalValue = this.value;
    },
    internalValue: function (newValue, oldValue) {
      this.$emit('input', this.internalValue);
      if (newValue === this.profile.id) return;

      if (isNil(newValue)) {
        this.profile = {
          type: this.type,
          primaryAddress: {},
          unlocked: true,
        };
      } else {
        const $vm = this;
        this.axios
          .get(this.serviceUrls.guest.get(this, newValue))
          .then((response) => {
            $vm.profile = {
              unlocked: false,
              ...response.data.data,
            };
          })
          .catch((error) => {
            $vm.profile = {
              type: $vm.type,
              primaryAddress: {},
              unlocked: true,
            };
          })
          .finally(() => { this.guestLoaded = true; });
      }
    },
  },

  created: function () {
    if (isNil(this.internalValue)) {
      this.profile = {
        type: this.type,
        primaryAddress: {},
        unlocked: true,
      };
    } else {
      this.__pauseAutosave('profile');
      const $vm = this;
      this.axios
        .get(this.serviceUrls.guest.get(this, this.internalValue))
        .then((response) => {
          $vm.profile = {
            unlocked: false,
            ...response.data.data,
          };
        })
        .catch((error) => {
          $vm.profile = {
            type: $vm.type,
            primaryAddress: {},
            unlocked: true,
          };
        })
        .finally(() => {
          this.__continueAutosave('profile');
          this.guestLoaded = true;
        });
    }
  },

  methods: {
    isEmpty: function (val) {
      return isEmpty(val);
    },
    isEmptyProfile: function (val) {
      if (!isEmpty(val.companyName)) return false;
      if (!isEmpty(val.namePrefix)) return false;
      if (!isEmpty(val.lastName)) return false;
      if (!isEmpty(val.firstName)) return false;
      if (!isEmpty(val.nameSuffix)) return false;
      if (!isEmpty(val.primaryEMailAddress)) return false;
      if (!isEmpty(val.primaryPhoneNumber)) return false;
      if (!isEmpty(val.primaryAddress.address)) return false;
      if (!isEmpty(val.primaryAddress.zipCode)) return false;
      if (!isEmpty(val.primaryAddress.city)) return false;
      if (!isEmpty(val.primaryAddress.country)) return false;
      if (!isEmpty(val.primaryCommunicationLanguage)) return false;
      if (!isEmpty(val.nationality)) return false;
      return true;
    },
    updateSearch: function () {
      const guestSearch = this.$refs.guestSearch;
      if (!isNil(guestSearch)) {
        guestSearch.updateValue(null, true);
      }
      // this.setTimeout(() => {
      //   this.$refs.guestSearch.updateValue(null, true);
      // }, 250);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/lib/inputgroups/GuestProfileEditor.vue